/**
 * The caller's Household and its own values (Household spec, phase D): each
 * member's colour and list order, and the bank list (short name + Minimum
 * Balance default). Everything that used to be hard-coded for one family.
 */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api/client";
import { apiUrl } from "@/lib/api/config";
import { getDefaultColor } from "@/lib/accounts/colors";

export interface HouseholdMember {
  id: string;
  name: string;
  role: string;
  isActive: boolean;
  color: string | null;
  displayOrder: number | null;
}

export interface HouseholdBank {
  short: string;
  names: string[];
  minBalancePaise: number | null;
}

export interface HouseholdView {
  id: string;
  name: string;
  members: HouseholdMember[];
  banks: HouseholdBank[];
}

function firstName(name: string): string {
  return (name ?? "").trim().split(/\s+/)[0] ?? "";
}

/** Pure lookups over one Household payload (null → today's built-in fallbacks). */
export function householdLookups(view: HouseholdView | null | undefined) {
  const byFirst = new Map<string, HouseholdMember>();
  for (const m of view?.members ?? []) byFirst.set(firstName(m.name), m);
  const bankByName = new Map<string, HouseholdBank>();
  for (const b of view?.banks ?? []) for (const n of b.names) bankByName.set(n.trim().toLowerCase(), b);
  return {
    colorOf: (name: string) => byFirst.get(firstName(name))?.color ?? getDefaultColor(name),
    rankOf: (name: string) => byFirst.get(firstName(name))?.displayOrder ?? undefined,
    bankShort: (bankName: string) => bankByName.get((bankName ?? "").trim().toLowerCase())?.short,
    bankFor: (bankName: string) => bankByName.get((bankName ?? "").trim().toLowerCase()),
  };
}

export function useHousehold() {
  const query = useQuery<HouseholdView>({
    queryKey: ["household"],
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const res = await apiFetch(apiUrl("household"));
      if (!res.ok) throw new Error("Could not load the household");
      return res.json();
    },
  });
  // Stable lookups per payload, so memoised consumers recompute only when the
  // Household data actually changes.
  const lookups = useMemo(() => householdLookups(query.data), [query.data]);
  return { household: query.data ?? null, isLoading: query.isLoading, ...lookups };
}
